import { useState } from "react";
import { Play } from "lucide-react";
import { trackEvent } from "../utils/analytics";
import type { ProductDemoVideo as ProductDemoVideoData } from "../data/products";

interface ProductDemoVideoProps {
  demo?: ProductDemoVideoData;
  productName: string;
}

/**
 * Full playback mechanism (thumbnail gate, captions track, play/complete
 * analytics) built and ready — every product's `demo` is currently
 * undefined (no real ZenFresh app footage exists yet), so this renders an
 * honest "coming soon" panel instead of a video. Adaptive-bitrate streaming
 * and "CMS blocks publishing without captions" are backend/CMS claims this
 * static SPA can't fulfill — flagged N/A rather than faked.
 */
export default function ProductDemoVideo({ demo, productName }: ProductDemoVideoProps) {
  const [loadRequested, setLoadRequested] = useState(false);

  if (!demo) {
    return (
      <div className="flex aspect-video items-center justify-center rounded-2xl bg-ocean-900/5">
        <p className="px-6 text-center text-sm text-ocean-800/50">Demo video for {productName} coming soon.</p>
      </div>
    );
  }

  if (!loadRequested) {
    return (
      <button
        type="button"
        onClick={() => setLoadRequested(true)}
        aria-label={`Play ${productName} demo video`}
        className="group relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-2xl bg-ocean-900"
      >
        {demo.posterSrc && (
          <img src={demo.posterSrc} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        )}
        <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-teal-400 text-ocean-950 transition-transform group-hover:scale-110">
          <Play className="h-6 w-6" fill="currentColor" />
        </span>
      </button>
    );
  }

  return (
    <video
      controls
      autoPlay
      playsInline
      poster={demo.posterSrc}
      className="aspect-video w-full rounded-2xl bg-black"
      onPlay={() => trackEvent("product_demo_play", { product: productName })}
      onEnded={() => trackEvent("product_demo_complete", { product: productName })}
    >
      <source src={demo.src} />
      <track kind="captions" src={demo.captionsSrc} default />
    </video>
  );
}
